import React, { useState, useEffect } from 'react';
import { useCallRecording } from '../contexts/CallRecordingContext';
import { useUser } from '../contexts/UserContext';
import { useQueue } from '../contexts/QueueContext';
import { API_URL } from '../config';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  Alert,
  Snackbar,
  Paper,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import Grid2 from '@mui/material/Grid';
import { Download as DownloadIcon, Search as SearchIcon, FilterList as FilterIcon, Phone as PhoneIcon, PlayArrow as PlayIcon } from '@mui/icons-material';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} id={`call-tabpanel-${index}`} aria-labelledby={`call-tab-${index}`} {...other}>
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const CallManagement: React.FC = () => {
  const { recordings, loading, error, fetchRecordings, downloadRecording } = useCallRecording();
  const userContext = useUser();
  const queueContext = useQueue();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' as 'success' | 'error' | 'warning' | 'info' });
  const [playerDialog, setPlayerDialog] = useState<{ open: boolean; audioUrl: string; callId: string }>({ open: false, audioUrl: '', callId: '' });

  useEffect(() => {
    fetchRecordings();
  }, []);

  const handleDownloadRecording = async (callId: string) => {
    try {
      await downloadRecording(callId);
      setSnackbar({
        open: true,
        message: 'Kayıt indiriliyor...',
        severity: 'info'
      });
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'Kayıt indirilemedi',
        severity: 'error'
      });
    }
  };

  const handlePlayRecording = (callId: string) => {
    const baseURL = API_URL;
    const token = localStorage.getItem('token') || localStorage.getItem('accessToken');
    const audioUrl = `${baseURL}/calls/${callId}/recording/stream?token=${token}`;
    setPlayerDialog({ open: true, audioUrl, callId });
  };

  // Note: helper functions are defined below and used by CallTable

  // Sadece /calls ile gelen kayıtlar
  const filteredCalls = (Array.isArray(recordings) ? recordings : []).filter((call: any) => {
    const s = searchTerm.toLowerCase();
    const matchesSearch = (call.callerNumber || '').toLowerCase().includes(s) ||
      ((call.agentName || '') as string).toLowerCase().includes(s) ||
      ((call.queueName || '') as string).toLowerCase().includes(s);
    const matchesStatus = statusFilter === 'all' || call.status === statusFilter;
    const callDate = call.startTime ? new Date(call.startTime) : new Date(0);
    const from = dateFrom ? new Date(dateFrom) : null;
    const to = dateTo ? new Date(dateTo) : null;
    const matchesDate = (!from || callDate >= from) && (!to || callDate <= to);
    return matchesSearch && matchesStatus && matchesDate;
  });

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      <Typography variant="h4" gutterBottom>
        Çağrı Yönetimi
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid2 container spacing={2} alignItems="center">
            <Grid2 size={{ xs: 12, md: 3 }}>
              <TextField
                fullWidth
                label="Ara"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
                }}
              />
            </Grid2>
            <Grid2 size={{ xs: 12, md: 2 }}>
              <FormControl fullWidth>
                <InputLabel>Durum</InputLabel>
                <Select
                  value={statusFilter}
                  label="Durum"
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <MenuItem value="all">Tümü</MenuItem>
                  <MenuItem value="ringing">Çalıyor</MenuItem>
                  <MenuItem value="answered">Cevaplandı</MenuItem>
                  <MenuItem value="ended">Sonlandı</MenuItem>
                  <MenuItem value="missed">Kaçırıldı</MenuItem>
                  <MenuItem value="recording">Kayıt Ediliyor</MenuItem>
                </Select>
              </FormControl>
            </Grid2>
            <Grid2 size={{ xs: 12, md: 2 }}>
              <TextField
                fullWidth
                label="Başlangıç Tarihi"
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid2>
            <Grid2 size={{ xs: 12, md: 2 }}>
              <TextField
                fullWidth
                label="Bitiş Tarihi"
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid2>
            <Grid2 size={{ xs: 12, md: 1 }}>
              <Button
                variant="outlined"
                startIcon={<FilterIcon />}
                onClick={() => {
                  setSearchTerm('');
                  setStatusFilter('all');
                  setDateFrom('');
                  setDateTo('');
                }}
              >
                Temizle
              </Button>
            </Grid2>
          </Grid2>
        </CardContent>
      </Card>

      {/* Tablo: Tüm çağrılar (kaçanlar dahil) */}
      <CallTable calls={filteredCalls} onDownloadRecording={handleDownloadRecording} onPlayRecording={handlePlayRecording} users={userContext?.users || []} queues={queueContext?.queues || []} />

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Audio Player Dialog */}
      <Dialog open={playerDialog.open} onClose={() => setPlayerDialog({ ...playerDialog, open: false })} maxWidth="sm" fullWidth>
        <DialogTitle>Kayıt Dinle</DialogTitle>
        <DialogContent>
          <audio controls autoPlay style={{ width: '100%' }} src={playerDialog.audioUrl} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPlayerDialog({ ...playerDialog, open: false })} color="primary">Kapat</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

// CallTable (uses users & queues passed from parent to resolve names)
interface CallTableProps {
  calls: any[];
  onDownloadRecording: (callId: string) => void;
  onPlayRecording: (callId: string) => void;
  users?: any[];
  queues?: any[];
}

const CallTable: React.FC<CallTableProps> = ({ calls, onDownloadRecording, onPlayRecording, users = [], queues = [] }) => {
  const resolveAgentName = (call: any) => {
    if (call.agentName) return call.agentName;
    const agentId = call.agentId;
    if (agentId) {
      if (typeof agentId === 'object' && 'firstName' in agentId) return `${agentId.firstName} ${agentId.lastName || ''}`.trim();
      if (typeof agentId === 'string') {
        const u = users.find((x: any) => x._id === agentId);
        if (u) return `${u.firstName} ${u.lastName}`;
        return agentId;
      }
    }
    return 'Bilinmiyor';
  };

  const resolveQueueName = (call: any) => {
    if (call.queueName) return call.queueName;
    const q = call.queueId;
    if (q) {
      if (typeof q === 'object' && 'name' in q) return q.name;
      if (typeof q === 'string') {
        const qq = queues.find((x: any) => x._id === q);
        if (qq) return qq.name;
        return q;
      }
    }
    return 'Bilinmiyor';
  };

  return (
    <TableContainer>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Arayan</TableCell>
            <TableCell>Aranan</TableCell>
            <TableCell>Temsilci</TableCell>
            <TableCell>Kuyruk</TableCell>
            <TableCell>Durum</TableCell>
            <TableCell>Süre</TableCell>
            <TableCell>Başlangıç</TableCell>
            <TableCell>Bitiş</TableCell>
            <TableCell>Kayıt</TableCell>
            <TableCell>İşlemler</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {calls.map((call) => (
            <TableRow key={call._id || call.id} hover>
              <TableCell>
                <Box display="flex" alignItems="center">
                  <PhoneIcon sx={{ mr: 1, color: 'text.secondary' }} />
                  {call.callerNumber || '-'}
                </Box>
              </TableCell>

              <TableCell>
                <Box display="flex" alignItems="center">
                  <PhoneIcon sx={{ mr: 1, color: 'text.secondary' }} />
                  {call.calledNumber || '-'}
                </Box>
              </TableCell>

              <TableCell>
                <Typography variant="body2">{resolveAgentName(call)}</Typography>
              </TableCell>

              <TableCell>
                <Typography variant="body2">{resolveQueueName(call)}</Typography>
              </TableCell>

              <TableCell>
                <Chip label={getStatusText(call.status)} color={getStatusColor(call.status)} size="small" />
              </TableCell>

              <TableCell>{typeof call.duration === 'number' ? formatDuration(call.duration) : '-'}</TableCell>

              <TableCell>{call.startTime ? new Date(call.startTime).toLocaleString('tr-TR') : '-'}</TableCell>

              <TableCell>{call.endTime ? new Date(call.endTime).toLocaleString('tr-TR') : '-'}</TableCell>

              <TableCell>
                {call.status === 'recording' ? (
                  <Chip label="Kayıt Ediliyor" color="info" size="small" />
                ) : call.recording?.hasRecording ? (
                  <Chip label="Mevcut" color="success" size="small" />
                ) : (
                  <Chip label="Yok" color="default" size="small" />
                )}
              </TableCell>

              <TableCell>
                {call.recording?.hasRecording && (
                  <>
                    <Tooltip title="Kayıt Dinle">
                      <IconButton onClick={() => onPlayRecording(call._id)} color="primary">
                        <PlayIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Kayıt İndir">
                      <IconButton onClick={() => onDownloadRecording(call._id)} color="secondary">
                        <DownloadIcon />
                      </IconButton>
                    </Tooltip>
                  </>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

// Helper functions
const getStatusColor = (status: string) => {
  switch (status) {
    case 'ringing': return 'warning';
    case 'answered': return 'success';
    case 'ended': return 'default';
    case 'missed': return 'error';
    case 'recording': return 'info';
    default: return 'default';
  }
};

const getStatusText = (status: string) => {
  switch (status) {
    case 'ringing': return 'Çalıyor';
    case 'answered': return 'Cevaplandı';
    case 'ended': return 'Sonlandı';
    case 'missed': return 'Kaçırıldı';
    case 'completed': return 'Cevaplandı';
    case 'failed': return 'Başarısız';
    case 'processing': return 'İşleniyor';
    case 'recording': return 'Kayıt Ediliyor';
    default: return 'Bilinmiyor';
  }
};

const formatDuration = (seconds: number) => {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
};

export default CallManagement;